import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Design System Constants - Fixed Spacing
const DESIGN_SYSTEM = {
  spacing: {
    xs: 'space-y-2',
    sm: 'space-y-3', 
    md: 'space-y-4',
    lg: 'space-y-6',
    xl: 'space-y-8'
  },
  container: {
    sm: 'max-w-4xl',
    md: 'max-w-5xl', 
    lg: 'max-w-6xl',
    xl: 'max-w-7xl'
  },
  padding: {
    sm: 'p-4 md:p-6',
    md: 'p-6 md:p-8',
    lg: 'p-8 md:p-10',
    xl: 'p-10 md:p-12'
  },
  text: {
    badge: 'text-sm font-semibold tracking-wide',
    heading1: 'text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight',
    heading2: 'text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight',
    heading3: 'text-2xl md:text-3xl font-bold tracking-tight',
    body: 'text-lg md:text-xl leading-relaxed',
    caption: 'text-base md:text-lg leading-relaxed'
  },
  cards: {
    sm: 'rounded-2xl bg-gradient-to-br from-dark-bg-lighter/80 to-dark-bg/80 backdrop-blur-md border border-white/10',
    md: 'rounded-3xl bg-gradient-to-br from-dark-bg-lighter/80 to-dark-bg/80 backdrop-blur-md border border-white/10',
    lg: 'rounded-3xl bg-gradient-to-br from-dark-bg-lighter/90 to-dark-bg/90 backdrop-blur-xl border border-white/15'
  },
  buttons: {
    primary: 'bg-gradient-to-r from-ripple-blue to-ripple-blue-light hover:from-ripple-blue/90 hover:to-ripple-blue-light/90 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-ripple-blue/25 border border-ripple-blue/30',
    secondary: 'bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/20 hover:border-white/30 text-white font-semibold rounded-xl transition-all duration-300'
  }
};

// Professional Web3 Icons
const WalletIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.5 4H5.5C4.12 4 3 5.12 3 6.5v11C3 18.88 4.12 20 5.5 20h13c1.38 0 2.5-1.12 2.5-2.5V6.5C21 5.12 19.88 4 18.5 4zM19 17.5c0 .28-.22.5-.5.5h-13c-.28 0-.5-.22-.5-.5V6.5c0-.28.22-.5.5-.5h13c.28 0 .5.22.5.5V17.5zM14.5 9h3c.83 0 1.5.67 1.5 1.5v2c0 .83-.67 1.5-1.5 1.5h-3c-.83 0-1.5-.67-1.5-1.5v-2c0-.83.67-1.5 1.5-1.5zm1.5 3h1v-1h-1v1z"/>
  </svg>
);

const CurrencyIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
  </svg>
);

const SwapIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z"/>
  </svg>
);

const SpendIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
  </svg>
);

const ArrowIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const HowItWorks: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const steps = [
    {
      step: "01",
      title: "Connect Your Wallet",
      description: "Link your XUMM wallet or create a new one. Your keys, your crypto — we never hold custody.",
      icon: <WalletIcon className="w-6 h-6" />,
      color: "ripple-blue"
    },
    {
      step: "02", 
      title: "Fund with RLUSD",
      description: "Bridge stablecoins into XRP Ledger via RLUSD to enable yield strategies without volatility.",
      icon: <CurrencyIcon className="w-6 h-6" />,
      color: "accent-blue"
    },
    {
      step: "03",
      title: "Swap & Earn Yield",
      description: "Provide liquidity and earn returns on native XRPL AMM pools with institutional-level speed.",
      icon: <SwapIcon className="w-6 h-6" />,
      color: "ripple-teal"
    },
    {
      step: "04",
      title: "Spend in Real World", 
      description: "Use your yield gains through our debit card and merchant payment partners — crypto that works IRL.",
      icon: <SpendIcon className="w-6 h-6" />,
      color: "accent-sky"
    }
  ];

  return (
    <section id="how-it-works" className="relative py-8 md:py-12 lg:py-16 overflow-hidden">
      {/* Enhanced Light High-Tech Background */}
      <div className="absolute inset-0">
        {/* Light base gradient with depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-gray-50 to-slate-200"></div>
        
        {/* Layered light glass textures */}
        <div className="absolute inset-0">
          {/* Primary light glass layer */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-white/20 backdrop-blur-[1px]"></div>
          {/* Secondary light glass layer with different angle */}
          <div className="absolute inset-0 bg-gradient-to-tl from-slate-100/30 via-transparent to-blue-50/20 backdrop-blur-[0.5px]"></div>
        </div>
        
        {/* Enhanced geometric elements with glass effect */}
        <div className="absolute inset-0">
          <div className="absolute w-96 h-96 rounded-full bg-gradient-to-br from-ripple-teal/6 via-ripple-teal/3 to-transparent -top-48 -left-48 blur-3xl backdrop-blur-sm"></div>
          <div className="absolute w-80 h-80 rounded-full bg-gradient-to-br from-accent-sky/5 via-accent-sky/2 to-transparent -bottom-40 -right-40 blur-2xl backdrop-blur-sm"></div>
          {/* Additional floating elements */}
          <div className="absolute w-40 h-40 rounded-full bg-gradient-to-br from-teal-200/15 to-transparent top-1/4 left-1/3 blur-xl animate-pulse"></div>
          <div className="absolute w-32 h-32 rounded-full bg-gradient-to-br from-sky-200/15 to-transparent bottom-1/3 right-1/4 blur-lg animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>
        
        {/* High-tech grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `
            radial-gradient(circle at 1px 1px, rgba(59,130,246,0.3) 1px, transparent 0),
            linear-gradient(90deg, rgba(59,130,246,0.1) 1px, transparent 1px),
            linear-gradient(rgba(59,130,246,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px, 40px 40px, 40px 40px'
        }}></div>
        
        {/* Noise texture for premium feel */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.4'/%3E%3C/svg%3E")`
        }}></div>

        {/* Subtle animated shimmer effect */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 animate-shimmer"></div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
        
        {/* Light glass container for header */}
        <div className="relative backdrop-blur-sm bg-white/20 rounded-3xl border border-white/30 shadow-2xl shadow-black/5 p-8 md:p-12 mb-12">
          {/* Inner glow effect */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none"></div>
          
          {/* Section Header */}
          <motion.div
            className="text-center max-w-4xl mx-auto relative z-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] mb-6 text-gray-900 drop-shadow-sm">
              Four simple steps to{' '}
              <span className="bg-gradient-to-r from-ripple-blue via-ripple-blue-light to-ripple-teal bg-clip-text text-transparent drop-shadow-sm">
                financial freedom
              </span>
            </h2>
          </motion.div>
        </div>

        {/* Enhanced Steps Grid with premium light glass cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-10 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="relative group"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -6 }}
            >
              {/* Light glass card with enhanced effects */}
              <div className="relative bg-white/40 backdrop-blur-xl border border-white/30 rounded-2xl p-8 min-h-[280px] transition-all duration-300 shadow-xl shadow-black/5 group-hover:bg-white/60 group-hover:border-white/50 group-hover:shadow-2xl group-hover:shadow-black/10 flex flex-col">
                
                {/* Inner highlight */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

                {/* Content */}
                <div className="relative z-10 flex-1 flex flex-col">
                  {/* Step Number Badge */}
                  <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-br from-ripple-blue to-ripple-blue-light text-white text-sm font-bold rounded-full flex items-center justify-center shadow-lg">
                    {step.step}
                  </div>

                  {/* Step Icon */}
                  <motion.div 
                    className="w-14 h-14 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-gradient-to-br from-ripple-blue/20 via-ripple-blue/10 to-transparent backdrop-blur-md border border-ripple-blue/20 shadow-lg shadow-ripple-blue/10 group-hover:shadow-ripple-blue/20 transition-all duration-300"
                    whileHover={{ scale: 1.05, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="text-ripple-blue">
                      {step.icon}
                    </div>
                  </motion.div>

                  {/* Step Content */}
                  <div className="text-center space-y-4 flex-1 flex flex-col">
                    <h3 className="text-2xl font-semibold text-gray-900 group-hover:text-ripple-blue transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed flex-1">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Enhanced CTA Section with light glass morphism */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {/* Light glass container for CTA */}
          <div className="relative backdrop-blur-sm bg-white/20 rounded-3xl border border-white/30 shadow-2xl shadow-black/5 p-8 max-w-3xl mx-auto">
            {/* Inner glow effect */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none"></div>
            
            <div className="relative z-10 space-y-6">
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                Ready to transform your financial future? Join thousands of users already building wealth on XRP Ledger.
              </p>
              
              <motion.button
                className="px-10 py-4 bg-gradient-to-r from-ripple-blue to-ripple-blue-light text-white font-semibold rounded-2xl hover:shadow-xl hover:shadow-ripple-blue/30 transition-all duration-300 inline-flex items-center justify-center space-x-2 backdrop-blur-sm"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  window.location.href = '/app';
                }}
              >
                <span>Get Started</span>
                <motion.div
                  className="w-5 h-5"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowIcon className="w-5 h-5" />
                </motion.div>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks; 